import { EncodeMedium } from './EncodeContext.js';
export function isEncodeable(object) {
    if (typeof object !== 'object' || object === null) {
        return false;
    }
    return typeof object.encode === 'function';
}
const isDevelopment = process.env.NODE_ENV === 'development';
/**
 * Use this method to encode an object (might be an encodeable implementation) into a decodable structure
 */
export function encodeObject(obj, context) {
    // Go from most common types to less common types. This increases performance a lot!
    const t = typeof obj;
    if (t === 'string' || t === 'number' || t === 'boolean' || obj === null || obj === undefined) {
        return obj;
    }
    if (t === 'object') {
        if (typeof obj.encode === 'function') {
            return obj.encode(context);
        }
        if (obj.length !== undefined && Array.isArray(obj)) {
            const len = obj.length;
            const result = new Array(obj.length);
            for (let i = 0; i < len; i++) {
                result[i] = encodeObject(obj[i], context);
            }
            return result;
        }
        if (obj.size !== undefined && obj instanceof Map) {
            const encodedObj = {};
            if (context.medium === EncodeMedium.Database || isDevelopment) {
                // Only sort keys in database to avoid chaining data
                const keys = [...obj.keys()];
                if (keys.length !== 0) {
                    const firstKey = keys[0];
                    const firstType = typeof firstKey;
                    // check type of keys
                    if (firstType !== 'string' && firstType !== 'number') {
                        throw new Error(`Map keys must be strings or numbers. Got ${firstType}`);
                    }
                    for (const key of keys) {
                        if (typeof key !== firstType) {
                            throw new Error(`Map keys must be of the same type. Got ${firstType} and ${typeof key}`);
                        }
                    }
                    // sort keys
                    if (firstType === 'number') {
                        keys.sort((a, b) => a - b);
                    }
                    else {
                        keys.sort((a, b) => a.localeCompare(b));
                    }
                    for (const key of keys) {
                        const value = obj.get(key);
                        Object.defineProperty(encodedObj, key, { value: encodeObject(value, context), enumerable: true, configurable: true, writable: true });
                    }
                }
            }
            else {
                for (const [key, value] of obj) {
                    const k = encodeObject(key, context);
                    if (typeof k !== 'string' && typeof k !== 'number') {
                        throw new Error(`Map keys must be strings or numbers. Got ${k}`);
                    }
                    Object.defineProperty(encodedObj, k, { value: encodeObject(value, context), enumerable: true, configurable: true, writable: true });
                }
            }
            if (obj._isPatch) {
                return {
                    _isPatch: true,
                    changes: encodedObj,
                };
            }
            return encodedObj;
        }
        if (context.medium === EncodeMedium.Database || isDevelopment) {
            // Sort keys
            const keys = Object.keys(obj).sort(sortObjectKeysForEncoding);
            const encodedObj = {};
            for (const key of keys) {
                Object.defineProperty(encodedObj, key, { value: encodeObject(obj[key], context), enumerable: true, configurable: true, writable: true });
            }
            return encodedObj;
        }
        const encodedObj = {};
        for (const key of Object.keys(obj)) {
            Object.defineProperty(encodedObj, key, { value: encodeObject(obj[key], context), enumerable: true, configurable: true, writable: true });
        }
        return encodedObj;
    }
    if (t === 'symbol') {
        const key = Symbol.keyFor(obj);
        if (key) {
            return {
                $symbol: key,
            };
        }
    }
    // Failed to decode
    return obj;
}
export function sortObjectKeysForEncoding(a, b) {
    // Always have a fixed order for certain keys, and follow with alphabetical order
    // id, name, description, ...remaining
    if (a === b) {
        return 0;
    }
    if (a === 'id') {
        return -1;
    }
    if (b === 'id') {
        return 1;
    }
    if (a === 'name') {
        return -1;
    }
    if (b === 'name') {
        return 1;
    }
    if (a === 'description') {
        return -1;
    }
    if (b === 'description') {
        return 1;
    }
    return a.localeCompare(b);
}
//# sourceMappingURL=Encodeable.js.map